import React, { useContext, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useIntl } from 'react-intl'
import { Tabs, TabsProps } from 'antd'
import { useManualQuery } from 'graphql-hooks'

import { getPurchasedAddons } from '../../graphql/custom-queries'
import { track } from '../../services/analytics/analytics'
import { roleAdmin, selectAuthUserSlice } from '../../store/auth-user-slice'
import { notificationStore } from '../../context/notificationsContext/store'

import { getExtendedAddons } from '../Add-ons/common/addons'

import CircularProgress from '../../components/circular-progress'
import AddonFeedback from '../../components/addon-feedback'
import MyAddons from '../../components/my-addons'
import ChoiceAddons from '../../components/choice-addons'
import IntlMessages from '@vacationtracker/shared/components/utils/IntlMessages'

import { IGetPurchasedAddonsData } from '../../types/custom-queries'
import { AddonsStatusEnum, IAddon } from '@vacationtracker/shared/types/addons'
import { AddonsActiveTabEnum } from '@vacationtracker/shared/types/automations'
import { selectFeatureFlagsSlice } from '../../store/feature-flags-slice'
import { useAppSelector } from '../../store/hooks'

const AddonsPage = () => {
  const { formatMessage } = useIntl()
  const amIAdmin = useSelector(roleAdmin)
  const { actionNotifications } = useContext(notificationStore)
  const { authUser } = useAppSelector(selectAuthUserSlice)
  const isMounted = useRef(false)
  const { featureFlags } = useAppSelector(selectFeatureFlagsSlice)

  const [isLoading, setIsLoading] = useState(true)
  const [subscribedAddons, setSubscribedAddons] = useState<IAddon[]>([])
  const [activeTab, setActiveTab] = useState<AddonsActiveTabEnum>(AddonsActiveTabEnum.myAddons)
  
  const [ getPurchasedAddonsQuery ] = useManualQuery<IGetPurchasedAddonsData>(getPurchasedAddons)

  useEffect(() => {
    if (isLoading) {
      track('AUTOMATIONS_ACTIVE_VIEWED', {})
    }
  }, [isLoading])

  useEffect(() => {
    isMounted.current = true
    fetchData()
    return () => {
      isMounted.current = false
    }
  }, [actionNotifications])

  const fetchData = async () => {
    const response = await getPurchasedAddonsQuery()
    if (!isMounted.current) return
    if (!response.data || response.error) throw response.error

    const subscribedAddons = [
      ...(response.data?.getPurchasedAddons?.addons || []),
      ...(response.data?.getPurchasedAddons?.automations || []),
      ...(response.data?.getPurchasedAddons?.integrations || []),
    ]
    setSubscribedAddons(subscribedAddons as IAddon[])

    setIsLoading(false)
  }

  useEffect(() => {
    if (subscribedAddons.length === 0 && !isLoading) {
      setActiveTab(AddonsActiveTabEnum.discoveryMoreAddons)
    }
  }, [subscribedAddons, isLoading])

  const addonsEvent = getExtendedAddons(formatMessage, featureFlags)

  const handleChangeTab = (key: string) => {
    setActiveTab(key as AddonsActiveTabEnum)
    if (key === AddonsActiveTabEnum.discoveryMoreAddons) {
      track('DISCOVER_MORE_ADDONS_CLICKED', {})
    }
  }

  const addonsTabs: TabsProps['items'] = [
    {
      key: AddonsActiveTabEnum.myAddons,
      label: <IntlMessages id="automations.myAddons" />,
      children: <MyAddons
        subscribedAddons={subscribedAddons} 
        addonsEvent={addonsEvent}
        changeTab={(tab: AddonsActiveTabEnum) => {
          setActiveTab(tab)
        }}
      />,
    },
    {
      key: AddonsActiveTabEnum.discoveryMoreAddons,
      label: <IntlMessages id="automations.discoveryMoreAddons" />,
      children: <ChoiceAddons
        addonsEvent={addonsEvent}
        subscribedAddons={subscribedAddons.filter((addon) => addon.status !== AddonsStatusEnum.CANCELED)}
        allSubscribedAddons={subscribedAddons}
        formatMessage={formatMessage}
        amIAdmin={amIAdmin}
        authUserId={authUser.id}
      />,
    },
  ]

  return (
    <>
      {isLoading ?
        <CircularProgress /> :
        <div className='main-content'>
          <div className="main-content-header">
            <div className="main-content-header-title">
              <IntlMessages id="app.addons" />
            </div>
          </div>
          <div className="main-content-body">
            <div>
              <Tabs
                tabBarExtraContent={<AddonFeedback buttonStyle={{ float: 'right' }} page="automation-page-list" />}
                activeKey={activeTab}
                onTabClick={(key) => handleChangeTab(key)}
                items={addonsTabs}
              />
            </div>
          </div>
        </div>
      }
    </>
  )
}

export default AddonsPage
